import React, { useState, useEffect, useCallback } from "react";
import "./PharmacyStaffprofile.css";
import Sidebar from "../Sidebar/Sidebar";
import { cloudApi } from "../../api";
import { useNavigate, useParams } from "react-router-dom";

const PharmacyStaffprofile = () => {
  const navigate = useNavigate();
  const { staffId } = useParams();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [staffData, setStaffData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchStaffProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await cloudApi.get(`/staffmanager/staff/${staffId}`);
      setStaffData(res.data);
    } catch (err) {
      console.error("Error fetching staff:", err);
      setError(
        err?.response?.data?.detail || "Failed to load staff profile"
      );
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      navigate("/Login");
      return;
    }
    fetchStaffProfile();
  }, [staffId, navigate, fetchStaffProfile]);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  // ── Loading ──
  if (loading) {
    return (
      <div className="profile-layout">
        {!isMobile && <Sidebar />}
        <div className="profile-container">
          <p className="loading-text">Loading staff profile...</p>
        </div>
      </div>
    );
  }

  // ── Error / not found ──
  if (error || !staffData) {
    return (
      <div className="profile-layout">
        {!isMobile && <Sidebar />}
        <div className="profile-container">
          <div className="error-message">{error || "Staff not found"}</div>
          <button className="back-btn" onClick={() => navigate(-1)}>
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-layout">
      {!isMobile && <Sidebar />}

      <div className="profile-container">
        <button className="back-btn" onClick={() => navigate(-1)}>
          ← Back to Staff List
        </button>

        <div className="profile-card">

          {/* ── Header ── */}
          <div className="profile-header">
            <div className="profile-avatar">
              {staffData.staff_name.charAt(0).toUpperCase()}
            </div>
            <div className="profile-header-content">
              <h2>{staffData.staff_name}</h2>
              <span className={`role-badge role-${staffData.staff_role}`}>
                {staffData.staff_role.replace(/_/g, " ").toUpperCase()}
              </span>
              <span
                className={`status-badge ${
                  staffData.is_active ? "status-active" : "status-inactive"
                }`}
              >
                {staffData.is_active ? "● Active" : "● Inactive"}
              </span>
            </div>
          </div>

          {/* ── Contact Information ── */}
          <div className="profile-section">
            <h4>📋 Contact Information</h4>
            <div className="info-grid">
              <div className="info-item">
                <label>Email</label>
                <p>{staffData.staff_email}</p>
              </div>
              <div className="info-item">
                <label>Phone</label>
                <p>{staffData.staff_phone}</p>
              </div>
              <div className="info-item">
                <label>Role</label>
                <p>{staffData.staff_role.replace(/_/g, " ")}</p>
              </div>
              <div className="info-item">
                <label>Created On</label>
                <p>{formatDate(staffData.created_at)}</p>
              </div>
              <div className="info-item">
                <label>Status</label>
                <p
                  className={
                    staffData.is_active ? "text-active" : "text-inactive"
                  }
                >
                  {staffData.is_active ? "Active" : "Inactive"}
                </p>
              </div>
            </div>
          </div>

          {/* ── Location Information ──
               Renders only when the API returns populated pharmacy/branch objects.
               Both fields are now populated by the backend via selectinload +
               _build_staff_response mapping pharmacy_name → name and
               branch_name → name, branch_address → location.
          ── */}
          {staffData.pharmacy && (
            <div className="profile-section">
              <h4>📍 Location Information</h4>
              <div className="location-cards">

                <div className="location-card pharmacy-card">
                  <h5>🏥 Pharmacy</h5>
                  {/* pharmacy.name comes from Pharmacy.pharmacy_name */}
                  <p className="location-name">{staffData.pharmacy.name}</p>
                </div>

                <div
                  className={`location-card ${
                    staffData.branch ? "branch-card" : "main-card"
                  }`}
                >
                  <h5>
                    {staffData.branch ? "🌿 Branch" : "🏢 Main Location"}
                  </h5>
                  {staffData.branch ? (
                    <>
                      {/* branch.name comes from PharmacyBranch.branch_name */}
                      <p className="location-name">{staffData.branch.name}</p>
                      {/* branch.location comes from PharmacyBranch.branch_address */}
                      {staffData.branch.location && (
                        <p className="location-detail">
                          {staffData.branch.location}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="location-note">
                      Works at main pharmacy location
                    </p>
                  )}
                </div>

              </div>

              {/* location_type: "branch" | "main_pharmacy" */}
              {staffData.location_type && (
                <div
                  className={`location-type-badge ${staffData.location_type}`}
                >
                  {staffData.location_type === "branch"
                    ? "📌 Branch Staff"
                    : "🏢 Main Pharmacy Staff"}
                </div>
              )}
            </div>
          )}

          {/* ── Created By (Owner) ──
               Renders only when the API returns a populated created_by object.
               created_by.name and created_by.email come directly from the
               owner's User row (PharmacyStaff.created_by_id FK).
          ── */}
          {staffData.created_by && (
            <div className="profile-section">
              <h4>👑 Created By (Owner)</h4>
              <div className="owner-card">
                <div className="owner-avatar">
                  {staffData.created_by.name.charAt(0).toUpperCase()}
                </div>
                <div className="owner-details">
                  <p className="owner-name">{staffData.created_by.name}</p>
                  <p>
                    <strong>Email:</strong> {staffData.created_by.email}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Permissions ── */}
          <div className="profile-section">
            <h4>🔑 Permissions ({staffData.permissions?.length || 0})</h4>
            {staffData.permissions && staffData.permissions.length > 0 ? (
              <div className="permissions-grid">
                {staffData.permissions.map((perm, idx) => (
                  <span key={idx} className="permission-item">
                    ✓ {perm.replace(/_/g, " ").toUpperCase()}
                  </span>
                ))}
              </div>
            ) : (
              <p className="no-permissions">No permissions assigned</p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default PharmacyStaffprofile;